/**
 * AdminEmbedsHealth — one dashboard for every embed we ship.
 *
 * Answers ops's daily questions:
 *   1. Which embeds do we serve?          → the catalogue column
 *   2. Are they reachable right now?      → traffic-light Status column
 *   3. Are edits actually flowing through? → the "Live JSON" + iframe cols
 *
 * The API probe is done server-side (routes/embeds_health.py) so the UI
 * gets an atomic snapshot; the "Live preview" iframe hits the SAME
 * embed the external one.com page uses so what you see here IS what
 * the visitor sees.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ExternalLink, Eye, EyeOff, Radio, AlertTriangle, CheckCircle } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const LEVEL_STYLES = {
  green:  { bg: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200', icon: CheckCircle, dot: 'bg-emerald-400' },
  amber:  { bg: 'bg-amber-500/15   border-amber-500/40   text-amber-200',   icon: AlertTriangle, dot: 'bg-amber-400' },
  red:    { bg: 'bg-rose-500/15    border-rose-500/40    text-rose-200',    icon: AlertTriangle, dot: 'bg-rose-500' },
};

const StatusBadge = ({ status }) => {
  const s = LEVEL_STYLES[status?.level] || LEVEL_STYLES.red;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.bg}`}
          data-testid={`status-${status?.level || 'unknown'}`}>
      <Icon className="h-3 w-3" />
      {status?.message || '—'}
    </span>
  );
};

export default function AdminEmbedsHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState({});   // { rowKey: true }

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/embeds/health`, { headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load embed health');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rowKey = (r) => `${r.tenant}:${r.view}`;
  const togglePreview = (k) => setPreviewOpen((prev) => ({ ...prev, [k]: !prev[k] }));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6" data-testid="admin-embeds-health-page">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif tracking-tight">Embeds Health &amp; Preview</h1>
            <p className="text-sm text-slate-400 mt-1">
              Every widget we ship for ABMHC, FHBF, Faith Works TV, Bookshop &amp; friends.
              This page pings each API + verifies the static <code>.js</code>/<code>.html</code>
              is on disk. Live preview iframes show exactly what external visitors see.
            </p>
            {data?.backend_base && (
              <p className="text-xs text-slate-500 mt-1 font-mono">Probing: {data.backend_base}</p>
            )}
          </div>
          <Button onClick={load} disabled={loading} data-testid="refresh-embeds-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Probing…' : 'Refresh'}
          </Button>
        </div>

        {!data && !loading && (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="py-10 text-center text-slate-400">Loading…</CardContent>
          </Card>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {['green', 'amber', 'red'].map((level) => {
                const n = data.embeds.filter((r) => r.status.level === level).length;
                const s = LEVEL_STYLES[level];
                return (
                  <Card key={level} className={`border ${s.bg}`} data-testid={`summary-${level}`}>
                    <CardContent className="py-4 flex items-center gap-3">
                      <span className={`inline-block h-3 w-3 rounded-full ${s.dot}`} />
                      <div>
                        <div className="text-2xl font-bold">{n}</div>
                        <div className="text-xs uppercase tracking-wider opacity-80">
                          {level === 'green' ? 'Working' : level === 'amber' ? 'Degraded' : 'Broken / not built'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <Radio className="h-4 w-4 text-sky-400" /> Embed inventory ({data.count})
                </CardTitle>
                <CardDescription className="text-slate-400">
                  One row per embed. Hover a row for details. Click "Preview" to load the live embed.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2">Tenant / View</th>
                        <th className="text-left px-4 py-2">Public API</th>
                        <th className="text-left px-4 py-2">Script / iframe</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2">Cache</th>
                        <th className="text-right px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.embeds.map((r) => (
                        <React.Fragment key={rowKey(r)}>
                          <tr className="border-t border-slate-800 hover:bg-slate-800/30 align-top"
                              data-testid={`embed-row-${r.tenant}-${r.view}`}>
                            <td className="px-4 py-3">
                              <div className="font-medium text-white uppercase tracking-wider text-xs">{r.tenant}</div>
                              <div className="text-slate-300">{r.view}</div>
                              <div className="text-[11px] text-slate-500 mt-1">{r.public_host}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                              <div>{r.api}</div>
                              <div className="text-[10px] mt-1">
                                {r.api_probe?.status
                                  ? `HTTP ${r.api_probe.status} · ${r.api_probe.body_bytes}B · ${r.api_probe.latency_ms}ms`
                                  : r.api_probe?.error}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                              {r.script ? (
                                <div className={r.script_exists_on_disk ? '' : 'text-rose-400'}>
                                  {r.script}
                                  {!r.script_exists_on_disk && ' (missing on disk)'}
                                </div>
                              ) : (
                                <span className="italic text-slate-500">not built yet</span>
                              )}
                              {r.iframe && <div>{r.iframe}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <StatusBadge status={r.status} />
                            </td>
                            <td className="px-4 py-3 font-mono text-[10px] text-slate-400 max-w-[180px] truncate">
                              {r.api_probe?.cache_control || <span className="text-rose-400">none</span>}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                {r.script && (
                                  <a href={`${data.backend_base}${r.script}`} target="_blank" rel="noreferrer"
                                     className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">
                                    <ExternalLink className="h-3 w-3" /> .js
                                  </a>
                                )}
                                <a href={`${data.backend_base}${r.api}`} target="_blank" rel="noreferrer"
                                   className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-700 text-slate-300 hover:bg-slate-800"
                                   data-testid={`open-api-${r.tenant}-${r.view}`}>
                                  <ExternalLink className="h-3 w-3" /> JSON
                                </a>
                                {r.iframe && (
                                  <Button size="sm" variant="outline"
                                          className="border-sky-700 text-sky-300 hover:bg-sky-800/20"
                                          onClick={() => togglePreview(rowKey(r))}
                                          data-testid={`toggle-preview-${r.tenant}-${r.view}`}>
                                    {previewOpen[rowKey(r)]
                                      ? <><EyeOff className="h-3 w-3 mr-1" /> Hide</>
                                      : <><Eye   className="h-3 w-3 mr-1" /> Preview</>}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {previewOpen[rowKey(r)] && r.iframe && (
                            <tr className="border-t border-slate-800">
                              <td colSpan={6} className="p-4 bg-slate-950">
                                <div className="text-xs text-slate-500 mb-2 font-mono">
                                  {data.backend_base}{r.iframe}
                                </div>
                                <iframe
                                  src={`${data.backend_base}${r.iframe}`}
                                  title={`${r.tenant} ${r.view}`}
                                  className="w-full border border-slate-700 rounded bg-white"
                                  style={{ minHeight: 480 }}
                                  loading="lazy"
                                  data-testid={`iframe-preview-${r.tenant}-${r.view}`}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="text-xs text-slate-500 space-y-1 pt-2">
              <p>· Green = API 200 + script on disk + cache header set.</p>
              <p>· Amber = API works but embed script missing OR cache header missing (edits propagate slowly).</p>
              <p>· Red   = API not reachable / not built yet.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
